import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UsersDrawer from '../UsersDrawer';
import {
  FormControl,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { FlexBox } from '../../../global/FlexBox';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Tree } from '../Tree';
import { useCreateUser, useInternalDomainCheck } from '../../hooks';
import { UserWritePayload } from '@app/shared/types';
import { SuccessDrawer } from './SuccessDrawer';
import { useSnackbar } from 'notistack';
import { createPhoneChangeHandler } from '../../../../shared/utils';
import {
  CREATE_USER_DRAWER_ID,
  INPUT_MAX_LENGTH,
  USER_MANAGEMENT_WRITE,
  INTERNAL,
  EXTERNAL,
} from '../../constants';
import { useMinimizedDrawers } from '../../context/MinimizedDrawersContext';
import { useUserManagementContext } from '../../context/UserManagementContext';
import { Confirm } from '../Confirm';
import { userValidationSchema, UserFormData } from '../../validationSchemas';
import { PHONE_MAX_LENGTH } from '../../../../shared/constants';
import {
  getApiErrorMessage,
  getApiFieldErrorMessage,
} from '../../../../shared/api/getApiErrorMessage';
import {
  getUserOrganizationIds,
  resolveUserOrganization,
  toUserWritePayload,
} from '../../utils';

const ACCESS_TYPE_VALUE: Record<string, string> = {
  true: INTERNAL,
  false: EXTERNAL,
};

const DEFAULT_FORM_DATA = {
  attributes: [],
  email: '',
  isActive: true,
  organizationIds: [],
  otherInfo: '',
  username: '',
  phoneNumber: '',
  companyName: '',
};

export const CreateUserDrawer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    isUserCreateDrawerOpen,
    setIsUserCreateDrawerOpen,
    templates,
    organizations,
    setCurrentUserData,
    createUserData,
  } = useUserManagementContext();
  const {
    mutate: createUser,
    isPending,
    reset: resetMutation,
  } = useCreateUser();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { minimizeDrawer } = useMinimizedDrawers();

  const { loggedInUserData } = useUserManagementContext();

  const userHasWritePermission = loggedInUserData?.attributes.includes(
    USER_MANAGEMENT_WRITE,
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm<UserFormData>({
    resolver: yupResolver(userValidationSchema),
    defaultValues: DEFAULT_FORM_DATA,
  });

  const watchedEmail = watch('email');
  const isInternalDomain = useInternalDomainCheck(watchedEmail ?? '');

  useEffect(() => {
    if (isInternalDomain) {
      setValue('isInternal', true);
    }
  }, [isInternalDomain, setValue]);

  const onSubmit = async (
    data: UserWritePayload,
    isCreateAnother?: boolean,
  ) => {
    createUser(data, {
      onSuccess: createdUser => {
        setCurrentUserData(createdUser);
        if (isCreateAnother) {
          reset();
          setIsSuccess(false);
        } else {
          setIsSuccess(true);
        }
        enqueueSnackbar('User created successfully', {
          variant: 'success',
        });
      },

      onError: error => {
        const emailError = getApiFieldErrorMessage(error, 'email');
        const phoneNumberError = getApiFieldErrorMessage(error, 'phoneNumber');

        if (emailError) {
          setError('email', {
            type: 'manual',
            message: emailError,
          });
        }

        if (phoneNumberError) {
          setError('phoneNumber', {
            type: 'manual',
            message: phoneNumberError,
          });
        }

        enqueueSnackbar(getApiErrorMessage(error, 'Failed to create user'), {
          variant: 'error',
        });
      },
    });
  };

  const getPayload = (data: UserFormData): UserWritePayload =>
    toUserWritePayload(data);

  const handleCreateUser = () => {
    if (!userHasWritePermission) {
      setIsConfirmOpen(true);
      return;
    }
    const userPayload = getPayload(getValues());

    clearErrors();
    handleSubmit(() => onSubmit(userPayload))();
  };

  const resetForm = () => {
    reset();
    resetMutation();
    setIsSuccess(false);
  };

  const handleDrawerClose = () => {
    resetForm();
    setIsUserCreateDrawerOpen(false);
  };

  useEffect(() => {
    if (createUserData && isUserCreateDrawerOpen) {
      setValue('email', createUserData.email);
      setValue('username', createUserData.username);
      setValue('otherInfo', createUserData.otherInfo || '');
      setValue('organizationIds', getUserOrganizationIds(createUserData));
      setValue('attributes', createUserData.attributes || []);
      setValue('isActive', createUserData.isActive);
      if (createUserData.isInternal !== undefined) {
        setValue('isInternal', createUserData.isInternal);
      }
      setValue('phoneNumber', createUserData.phoneNumber || '');
      setValue('companyName', createUserData.companyName || '');
    }
  }, [createUserData, isUserCreateDrawerOpen, setValue]);

  const handleCreateAnotherUser = () => {
    if (!userHasWritePermission) {
      setIsConfirmOpen(true);
      return;
    }

    const data = getPayload(getValues());
    handleSubmit(() => onSubmit(data, true))();
  };

  const handleHide = () => {
    minimizeDrawer(
      CREATE_USER_DRAWER_ID,
      'Create User',
      getPayload(getValues()),
    );
    handleDrawerClose();
  };

  if (isSuccess) {
    return (
      <SuccessDrawer
        isOpen={isUserCreateDrawerOpen}
        handleDrawerClose={handleDrawerClose}
      />
    );
  }

  return (
    <UsersDrawer
      id="create-user-drawer"
      data-testid="create-user-drawer"
      title="Add New User"
      hasHide
      handleDrawerClose={handleDrawerClose}
      isOpen={isUserCreateDrawerOpen}
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel Adding User"
      cancelButtonText="Dismiss"
      handleHide={handleHide}
    >
      <FormControl
        style={{
          display: 'flex',
          width: '100%',
          gap: 32,
          flexDirection: 'column',
          marginBottom: 24,
        }}
      >
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Email Address"
              type="email"
              required
              label="Email Address"
              data-testid="create-user-email-input"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="username"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="User Name"
              label="User Name"
              required
              InputLabelProps={{ shrink: true }}
              data-testid="create-user-username-input"
              inputProps={{
                maxLength: INPUT_MAX_LENGTH,
                'data-testid': 'create-user-username-native-input',
              }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Phone Number"
              label="Phone Number"
              type="tel"
              data-testid="create-user-phone-input"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                maxLength: PHONE_MAX_LENGTH,
                'data-testid': 'create-user-phone-native-input',
              }}
              onChange={createPhoneChangeHandler(field.onChange)}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="companyName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Company Name"
              label="Company Name"
              data-testid="create-user-company-input"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="otherInfo"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Other Info"
              label="Other Info"
              data-testid="create-user-other-info-input"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="organizationIds"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const selectedOrgs = (field.value || []).map(orgId =>
              resolveUserOrganization({
                orgId,
                organizations: organizations?.data,
              }),
            );

            return (
              <Autocomplete
                multiple
                id="create-user-orgs-list"
                options={organizations?.data || []}
                getOptionLabel={option => option.name || ''}
                value={selectedOrgs}
                onChange={(_, value) => {
                  const orgIds = value.map(org => org.id || '');
                  field.onChange(orgIds);
                }}
                filterSelectedOptions
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Team"
                    placeholder={!field.value?.length ? 'Team' : ''}
                    required
                    data-testid="create-user-team-input"
                    InputLabelProps={{ shrink: true }}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            );
          }}
        />

        <Controller
          name="isInternal"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              variant="outlined"
              label="Access Category"
              data-testid="create-user-type-input"
              value={ACCESS_TYPE_VALUE[String(field.value)] ?? ''}
              onChange={e => {
                field.onChange(e.target.value === INTERNAL);
              }}
              select
              InputLabelProps={{ shrink: true }}
              required
              disabled={isInternalDomain}
              error={!!error}
              helperText={error?.message}
            >
              <MenuItem value={INTERNAL}>Internal</MenuItem>
              <MenuItem value={EXTERNAL}>External</MenuItem>
            </TextField>
          )}
        />

        <Controller
          name="attributes"
          control={control}
          render={({ field }) => (
            <Tree
              templates={templates}
              handleChange={field.onChange}
              value={field.value || []}
            />
          )}
        />
      </FormControl>

      <FlexBox sx={{ justifyContent: 'flex-end', gridColumnGap: 24 }}>
        <Button
          disabled={isPending}
          variant="contained"
          color="primary"
          onClick={handleCreateUser}
          startIcon={
            isPending ? (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginRight: 8 }}
              />
            ) : null
          }
        >
          Create User
        </Button>
        <Button
          disabled={isPending}
          variant="contained"
          color="primary"
          onClick={handleCreateAnotherUser}
          startIcon={
            isPending ? (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginRight: 8 }}
              />
            ) : null
          }
        >
          Save and Create Another User
        </Button>
      </FlexBox>
      <Confirm
        isOpen={isConfirmOpen}
        title="User cannot be created"
        content="We could not create a new user. Please try again"
        cancel="Cancel"
        onClose={() => setIsConfirmOpen(false)}
      />
    </UsersDrawer>
  );
};
